/**
 * CockpitKpis — Zona 2 do cockpit (mini-dashboard).
 *
 * KpiStrip com: dias no pipeline, tempo na etapa, nº de contatos, proximo
 * follow-up, valor estimado, + um cartao SPIN health (anel de completude do
 * diagnostico). O SPIN health usa o .health-ring canonico embutido como
 * `value` de um KPI dedicado.
 */
import React from "react";

import KpiStrip from "../../../components/KpiStrip";

import { HealthRing } from "./SpinHealth";
import { formatarDataLocal } from "../../../utils/dinheiro";

import { fmtMoeda } from "../crmHelpers";

function fmtFollowUp(iso) {
  if (!iso) return "—";
  try {
    // proximo_follow_up vem como date (YYYY-MM-DD) ou datetime ISO.
    const d = new Date(iso.length <= 10 ? `${iso}T00:00:00` : iso);
    return d.toLocaleDateString("pt-BR");
  } catch {
    return "—";
  }
}

function CockpitKpis({ metricas, valorEstimado, orcamentos }) {
  // Vale o orcamento MAIS RECENTE vinculado — mesma regra que o total do
  // pipeline usa (`_valor_do_lead_por_fonte` no backend).
  const valorOrcado = (() => {
    const lista = Array.isArray(orcamentos) ? orcamentos : [];
    if (lista.length === 0) return null;
    const v = Number(lista[0]?.valor_total);
    return Number.isFinite(v) ? v : null;
  })();
  const m = metricas || {};
  const spinTotal = m.spin_completude?.total ?? 0;

  // Array pequeno e barato — sem useMemo (KpiStrip nao depende de identidade).
  const items = [
    {
      label: "Dias no pipeline",
      value: m.dias_pipeline ?? 0,
      sub: "desde a criação",
      key: "pipeline",
    },
    {
      label: "Tempo na etapa",
      value: `${m.dias_na_etapa ?? 0}d`,
      sub: "etapa atual",
      key: "etapa",
    },
    {
      label: "Contatos",
      value: m.total_contatos ?? 0,
      // V10: aqui aparecia "N eventos" — contagem da tabela de auditoria,
      // que nasce em 1 no lead novo e fazia o cartao ler "0 / 1". Quem
      // trabalha o lead precisa saber quando foi a ultima conversa.
      sub: m.ultimo_contato
        ? `último em ${formatarDataLocal(m.ultimo_contato.slice(0, 10))}`
        : "nenhum registrado",
      key: "contatos",
    },
    {
      label: "Próximo follow-up",
      value: fmtFollowUp(m.proximo_follow_up),
      sub: m.tarefas_abertas
        ? `${m.tarefas_abertas} tarefa(s) aberta(s)`
        : "sem tarefas abertas",
      key: "follow",
    },
    // B1 (auditoria 2026-08-07): o cockpit mostrava "R$ 45.000" (a
    // estimativa que o vendedor digitou na entrada do lead) enquanto o
    // orcamento real do mesmo lead era R$ 4.200 — e o pipeline somava um
    // dos dois sem dizer qual. Estimativa e proposta sao coisas
    // diferentes; quando as duas existem, as duas aparecem.
    {
      label: valorOrcado != null ? "Valor orçado" : "Valor estimado",
      value: fmtMoeda(valorOrcado != null ? valorOrcado : valorEstimado),
      sub:
        valorOrcado != null && Number(valorEstimado) > 0
          ? `estimativa inicial: ${fmtMoeda(valorEstimado)}`
          : valorOrcado == null
            ? "sem orçamento ainda"
            : undefined,
      key: "valor",
    },
    {
      label: "Diagnóstico SPIN",
      value: <HealthRing total={spinTotal} max={4} />,
      sub: `${spinTotal}/4 estágios`,
      key: "spin",
    },
  ];

  return <KpiStrip items={items} />;
}

export default CockpitKpis;
